import {
  Button,
  CircularProgress,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  FormHelperText,
  Tooltip,
} from "@mui/material";
import { Alert } from "@mui/material";
import SelectEntity from "components/SelectEntity";
import useGlobalContext from "hooks/globalData";
import { pick } from "lodash";
import { useMemo } from "react";
import { Field, FormProps, withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import {
  BaseFilter,
  ITemplateAdmin,
  ListRequestOfBaseFilter,
  ListResponseOfAdminMenuType,
  TemplateSaveRequest,
} from "services/KnowfullyAdminClient";
import {
  CommonFormProps,
  TemplateTypeIds,
} from "types/common-types";
import DirtyFormSpy from "components/DirtyFormSpy";
import ContextOrganizationField, {
  ContextOrganizationFieldMode,
} from "components/ContextOrganizationField";

const { Form } = withTypes<TemplateSaveRequest>();

const useStyles = () => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
});

const ContentTemplateDetailsForm = ({
  onDirty,
  onClean,
  onSubmit,
  entity: template,
}: CommonFormProps<ITemplateAdmin, TemplateSaveRequest> &
  FormProps<TemplateSaveRequest>) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles();
  const { templateService } = useGlobalContext();

  const init = useMemo(
    () => ({
      ...pick(template ?? {}, [
        "id",
        "organizationId",
        "templateTypeId",
        "name",
        "description",
        "templateEntityId",
        "codeConstant",
        "placeholderName",
      ]),
    }),
    [template]
  );

  const fetchedTemplateTypes = useQuery<ListResponseOfAdminMenuType>(
    ["templateTypes"],
    () =>
      templateService.getTemplateTypes(
        new ListRequestOfBaseFilter({
          sortField: "name",
          parameters: BaseFilter.fromJS({
            excludedIds: [TemplateTypeIds.SkuAgreements],
          }),
        })
      )
  );

  const templateTypePageId = useMemo(
    () => fetchedTemplateTypes.data?.data?.find((tp) => tp.name === "Page")?.id,
    [fetchedTemplateTypes]
  );

  const fetchedEntityTypes = useQuery<ListResponseOfAdminMenuType>(
    ["entityTypes"],
    () =>
      templateService.getEntityTypes(
        new ListRequestOfBaseFilter({ sortField: "name" })
      )
  );

  const fetchedCodeConstants = useQuery<ListResponseOfAdminMenuType>(
    ["codeConstants"],
    () =>
      templateService.getCodeConstants(
        new ListRequestOfBaseFilter({
          sortField: "name",
        })
      )
  );

  return useMemo(
    () => (
      <Form
        onSubmit={onSubmit}
        initialValues={init}
        validate={(values) => {
          if (
            values.templateTypeId === templateTypePageId &&
            !values.codeConstant
          ) {
            return { codeConstant: "Required" };
          }
          if (
            values.templateTypeId !== undefined &&
            values.templateTypeId !== templateTypePageId &&
            !values.placeholderName
          ) {
            return { placeholderName: "Required" };
          }
        }}
        render={({
          handleSubmit,
          submitting,
          submitError,
          values,
          form: { reset, change },
          dirty,
        }) => {
          const requiredField = (value: string | undefined) =>
            value ? undefined : "Required";

          const isNotPage = () =>
            values.templateTypeId !== undefined &&
            values.templateTypeId !== templateTypePageId;

          return (
            <form onSubmit={handleSubmit}>
              <DirtyFormSpy onDirty={onDirty} onClean={onClean} />
              <Grid container spacing={2}>
                <ContextOrganizationField
                  values={values}
                  formChange={change}
                  mode={
                    template
                      ? ContextOrganizationFieldMode.EditManuallyTriggered
                      : ContextOrganizationFieldMode.SetByHeader
                  }
                />
                <Field name="name" required validate={requiredField}>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "nameLabel" })}
                        InputLabelProps={{ required: true }}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                        data-cy="nameLabelText"
                      />
                    </Grid>
                  )}
                </Field>

                <Field name="templateTypeId" required validate={requiredField}>
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectEntity
                          fetchedData={fetchedTemplateTypes}
                          labelField="name"
                          label={t({ id: "templateTypeLabel" })}
                          required={true}
                          {...input}
                          onChange={(id?: string) => {
                            if (templateTypePageId === id) {
                              change("placeholderName", undefined);
                            } else {
                              change("codeConstant", undefined);
                            }
                            input.onChange(id);
                          }}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                          data-cy="templateTypeDropDown"
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                <Field
                  name="templateEntityId"
                  required
                  validate={requiredField}
                >
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <SelectEntity
                          fetchedData={fetchedEntityTypes}
                          labelField="name"
                          label={t({ id: "entityLabel" })}
                          required={true}
                          {...input}
                          textFieldProps={{
                            error: meta.error && meta.submitFailed,
                          }}
                          data-cy="entityTypeDropDown"
                        />
                        {meta.error && meta.submitFailed && (
                          <FormHelperText>{meta.error}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Field>

                <Field
                  name="codeConstant"
                  required={values.templateTypeId === templateTypePageId}
                >
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <Tooltip
                        title={
                          isNotPage()
                            ? t({
                                id: "codeConstantDisabledForNotPageMessage",
                              })
                            : ""
                        }
                      >
                        <FormControl
                          fullWidth
                          error={meta.error && meta.submitFailed}
                        >
                          <SelectEntity
                            fetchedData={fetchedCodeConstants}
                            labelField="name"
                            label={t({ id: "codeConstantLabel" })}
                            required={
                              values.templateTypeId === templateTypePageId
                            }
                            disabled={
                              isNotPage() || values.templateTypeId == null
                            }
                            {...input}
                            textFieldProps={{
                              error: meta.error && meta.submitFailed,
                            }}
                            data-cy="codeConstantDropDown"
                          />
                          {meta.error && meta.submitFailed && (
                            <FormHelperText>{meta.error}</FormHelperText>
                          )}
                        </FormControl>
                      </Tooltip>
                    </Grid>
                  )}
                </Field>
                <Field name="placeholderName" required={isNotPage()}>
                  {({ input, meta }) => (
                    <Tooltip
                      title={
                        values.templateTypeId === templateTypePageId
                          ? t({
                              id: "placeholderNameDisabledForPageMessage",
                            })
                          : ""
                      }
                    >
                      <Grid item xs={12}>
                        <TextField
                          variant="standard"
                          label={t({ id: "placeholderNameLabel" })}
                          fullWidth
                          InputLabelProps={{
                            required: isNotPage(),
                          }}
                          disabled={
                            values.templateTypeId === templateTypePageId ||
                            values.templateTypeId == null
                          }
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                          data-cy="placeholderNameText"
                        />
                      </Grid>
                    </Tooltip>
                  )}
                </Field>

                <Field name="description">
                  {({ input, meta }) => (
                    <Grid item xs={12}>
                      <TextField
                        variant="standard"
                        fullWidth
                        label={t({ id: "descriptionLabel" })}
                        error={meta.error && meta.submitFailed}
                        helperText={
                          meta.error && meta.submitFailed && meta.error
                        }
                        {...input}
                        data-cy="descriptionText"
                      />
                    </Grid>
                  )}
                </Field>

                {submitError && (
                  <Alert sx={styles.feedback} severity="error">
                    {submitError}
                  </Alert>
                )}
              </Grid>

              <DialogActions>
                {submitting && <CircularProgress size={24} />}
                <Button
                  disabled={submitting || !dirty}
                  onClick={() => {
                    reset();
                  }}
                  data-cy="cancelButton"
                >
                  {t({ id: "cancelButton" })}
                </Button>

                <Button
                  type="submit"
                  color="primary"
                  disabled={submitting || !dirty}
                  data-cy="saveButton"
                >
                  {t({ id: "saveButton" })}
                </Button>
              </DialogActions>
            </form>
          );
        }}
      />
    ),
    [
      onSubmit,
      init,
      templateTypePageId,
      onDirty,
      onClean,
      template,
      styles.feedback,
      t,
      fetchedTemplateTypes,
      fetchedEntityTypes,
      fetchedCodeConstants,
    ]
  );
};

export default ContentTemplateDetailsForm;
